import { inject, Injectable } from '@angular/core';
import type {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';
import {
  isListBackCacheNavigationState,
  isListRefreshNavigationState,
} from '../utils/list-navigation.util';
import { ListNavigationContextService } from './list-navigation-context.service';
import { ListRouteReattachService } from './list-route-reattach.service';
import { RouteReuseStoreService } from './route-reuse-store.service';

@Injectable()
export class ListRouteReuseStrategy implements RouteReuseStrategy {
  private readonly navigationContext = inject(ListNavigationContextService);
  private readonly listReattach = inject(ListRouteReattachService);
  private readonly reuseStore = inject(RouteReuseStoreService);

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return route.data['reuseList'] === true;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const key = this.routeKey(route);
    if (key) {
      this.reuseStore.set(key, handle);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (route.data['reuseList'] !== true) {
      return false;
    }

    const key = this.routeKey(route);
    if (!key || !this.reuseStore.has(key)) {
      return false;
    }

    const state = this.navigationContext.getState();

    if (isListRefreshNavigationState(state)) {
      this.reuseStore.remove(key);
      return false;
    }

    const isBackNavigation =
      this.navigationContext.getTrigger() === 'popstate' ||
      isListBackCacheNavigationState(state);

    if (!isBackNavigation) {
      this.reuseStore.remove(key);
      return false;
    }

    return true;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.routeKey(route);
    if (!key) {
      return null;
    }
    const handle = this.reuseStore.get(key);
    if (handle) {
      this.listReattach.markPendingReattach();
    }
    return handle;
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  private routeKey(route: ActivatedRouteSnapshot): string | null {
    const path = route.routeConfig?.path;
    return path ?? null;
  }
}
